import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SgwtAccountCenter } from '@sgwt/sgwt-widgets-react';
import { useApplicationState } from '../../contexts/ApplicationContext';
import { useSession } from '../../contexts/SessionContext';
import { UserPermissions } from '../../types';
import { ProtectedRoute } from '../shared/common/ProtectedRoute';
import { useRequestPriority } from '../../contexts/RequestPriorityContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';

export const Header: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { userApplicationsCount } = useApplicationState();
  const location = useLocation();
  const { status } = useGlobalState();
  const { getHomePathname } = useSession();
  const { setApplicationsStatus, setTotalStatus } = useRequestPriority();

  const platformizationNavigation: JSX.Element = (
    <>
      <li className="navbar-item">
        <NavLink
          onClick={() => {
            setApplicationsStatus('shouldLoad');
            setTotalStatus('shouldCancel');
          }}
          className={'navbar-link' + (location.pathname.startsWith('/explore/') ? ' active' : '')}
          to={{
            pathname: getHomePathname(),
          }}>
          Explore
        </NavLink>
      </li>
      <li className="nav-item">
        <NavLink
          className={'navbar-link' + (location.pathname.startsWith('/reporting/') ? ' active' : '')}
          to={{
            pathname: '/reporting/compliance',
          }}>
          Reporting
        </NavLink>
      </li>
      <li className="nav-item">
        <NavLink className="navbar-link" to={{ pathname: '/profile' }}>
          My profile
        </NavLink>
      </li>
      <ProtectedRoute
        requiredPermissions={[UserPermissions.ADMIN, UserPermissions.MODERATOR]}
        exceptions={userApplicationsCount !== undefined && userApplicationsCount > 1}>
        <li className="nav-item">
          <NavLink className="navbar-link" to={{ pathname: '/certifications' }}>
            Certifications
          </NavLink>
        </li>
      </ProtectedRoute>
      <li className="nav-item">
        <a
          href={window.globalConfiguration.url.documentation}
          className="navbar-link"
          title="Go to Platformization documentation"
          target="_blank"
          rel="noopener noreferrer">
          Documentation
        </a>
      </li>
      <ProtectedRoute
        requiredPermissions={[
          UserPermissions.ADMIN,
          UserPermissions.MODERATOR,
          UserPermissions.AUDITOR,
          UserPermissions.REVIEWER,
          UserPermissions.ARCHITECT,
        ]}>
        <li className="nav-item">
          <NavLink
            className="navbar-link"
            to={{
              pathname: '/administration',
            }}>
            Administration
          </NavLink>
        </li>
      </ProtectedRoute>
    </>
  );

  if (status?.global === 'maintenance') {
    return null;
  }

  return (
    <nav className="navbar border-bottom border-top">
      <div className="navbar-title">
        <Link
          to={{
            pathname: getHomePathname(),
          }}
          className="navbar-title-link">
          <div className="navbar-logo">
            <img
              src="https://shared.sgmarkets.com/assets/images/socgen_logo_full.svg"
              height="32"
              alt="SG Markets"
              className="d-none d-md-block"
            />
            <img
              src="https://shared.sgmarkets.com/assets/images/socgen_logo_muted.svg"
              height="32"
              alt="SG Markets"
              className="d-block d-md-none"
            />
          </div>
          <div className="navbar-title-divider"></div>
          <div className="navbar-service-name">Platformization</div>
        </Link>
        <button
          className={`navbar-menu-btn btn btn-flat btn-xl btn-icon flex-center ${isExpanded ? 'show' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
          data-bs-toggle="dropdown"
          data-bs-offset="0,4"
          aria-expanded={isExpanded}>
          <svg
            className="icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="6" width="20" height="2" fill="currentColor"></rect>
            <rect x="2" y="11" width="20" height="2" fill="currentColor"></rect>
            <rect x="2" y="16" width="20" height="2" fill="currentColor"></rect>
          </svg>
        </button>
        <ul
          className={`navbar-menu-dropdown dropdown-menu w-100 ${isExpanded ? 'show' : ''}`}
          style={{ zIndex: 1021 }}
          data-bs-popper={isExpanded ? 'static' : 'none'}>
          {platformizationNavigation}
        </ul>
      </div>
      <div className="navbar-content">
        <ul className="navbar-navigation m-0">{platformizationNavigation}</ul>
      </div>
      <div className="navbar-toolbar">
        <SgwtAccountCenter
          mode="sg-markets"
          authentication="sg-connect"
          language="en"
          available-languages="en"
          navigate-as={undefined}
          hideServicesLink
          showSignInButton
          showLanguagesSelection
        />
      </div>
    </nav>
  );
};
